import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { OdlBeforeCloseEvent } from '@odilo/ui-components';
import { NavigateService } from '../../services/navigate/navigate';
import { PaymentStatusService } from '../../services/payment-status/payment-status';

/**
 * Shown when the PPU licence could not be paid past 10% progress. The OTK has already returned
 * the loan by then, so the only way out is back to the originating application.
 */
@Component({
  selector: 'nr-payment-blocked-dialog',
  imports: [TranslatePipe],
  templateUrl: './payment-blocked-dialog.html',
  styleUrl: './payment-blocked-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PaymentBlockedDialogComponent {
  private static readonly DESCRIPTION_KEYS = {
    PAYMENT_ERROR: 'PPU_BLOCKED_DIALOG.PAYMENT_ERROR',
    CHECKOUT_NOT_FOUND: 'PPU_BLOCKED_DIALOG.CHECKOUT_NOT_FOUND',
  } as const;

  private readonly paymentStatusService = inject(PaymentStatusService);
  private readonly navigateService = inject(NavigateService);

  readonly isBlocked = this.paymentStatusService.isBlocked;
  readonly hasExit = this.navigateService.hasBackUrl();
  readonly descriptionKey = computed(() => {
    const reason = this.paymentStatusService.paymentReason();
    // Any reason we don't recognise still has to say something: the dialog has no way out.
    return reason === 'CHECKOUT_NOT_FOUND'
      ? PaymentBlockedDialogComponent.DESCRIPTION_KEYS.CHECKOUT_NOT_FOUND
      : PaymentBlockedDialogComponent.DESCRIPTION_KEYS.PAYMENT_ERROR;
  });

  // The loan has already been returned, so this is a terminal state: dismissing it would leave
  // the reader inside a resource they are no longer entitled to.
  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  preventClose(event: Event): void {
    if (event instanceof OdlBeforeCloseEvent && event.detail.source !== 'programmatic') {
      event.preventDefault();
    }
  }

  goToPlatform(): void {
    this.navigateService.goToPlatform();
  }
}
